import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Simpler metric data structure
interface MetricData {
  loading: boolean;
  lastFetched: number | null; // timestamp
  response: any; // Store the raw API response
}

// Define the metrics state with simpler structure
interface MetricsState {
  pendingOrders: MetricData;
  expiringQuotes: MetricData;
  loadsShipping: MetricData;
  loadsInTransit: MetricData;
  currentAccrual: MetricData;
  outstandingInvoices: MetricData;
  deliveryPerformance: MetricData;
  costPerLB: MetricData;
}

// Create a base metric object for cleaner initialization
const createBaseMetric = (): MetricData => ({
  loading: false,
  lastFetched: null,
  response: null,
});

// Initial state
const initialState: MetricsState = {
  pendingOrders: createBaseMetric(),
  expiringQuotes: createBaseMetric(),
  loadsShipping: createBaseMetric(),
  loadsInTransit: createBaseMetric(),
  currentAccrual: createBaseMetric(),
  outstandingInvoices: createBaseMetric(),
  deliveryPerformance: createBaseMetric(),
  costPerLB: createBaseMetric(),
};

// Create the metrics slice
const metricsSlice = createSlice({
  name: "metrics",
  initialState,
  reducers: {
    // Loading state reducers
    setMetricLoading: (
      state,
      action: PayloadAction<{ metric: keyof MetricsState; loading: boolean }>,
    ) => {
      const { metric, loading } = action.payload;
      state[metric].loading = loading;
    },

    // Data update reducers
    updateMetric: (
      state,
      action: PayloadAction<{ metric: keyof MetricsState; data: any }>,
    ) => {
      const { metric, data } = action.payload;

      // Only update response if data is not null
      if (data !== null) {
        state[metric].response = data;
        state[metric].lastFetched = Date.now();
      }

      // Always turn off loading
      state[metric].loading = false;
    },
  },
});

export const { setMetricLoading, updateMetric } = metricsSlice.actions;

export default metricsSlice.reducer;
